import { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const SelectionContext = createContext();

// Provider component
export const SelectionProvider = ({ children }) => {
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Initialize with current week
    const now = new Date();
    const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay()));
    return startOfWeek.toISOString().split('T')[0];
  });

  
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedValet, setSelectedValet] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Persist selections to localStorage
  useEffect(() => {
    const savedState = localStorage.getItem('odvSelections');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.selectedWeek) setSelectedWeek(parsed.selectedWeek);
        if (parsed.selectedClient) setSelectedClient(parsed.selectedClient);
        if (parsed.selectedValet) setSelectedValet(parsed.selectedValet);
        if (parsed.selectedLocation) setSelectedLocation(parsed.selectedLocation);
      } catch (e) {
        console.error('Failed to parse saved selections:', e);
      }
    }
  }, []);

  // Save selections to localStorage when they change
  useEffect(() => {
    const state = {
      selectedWeek,
      selectedClient,
      selectedValet,
      selectedLocation
    };
    localStorage.setItem('odvSelections', JSON.stringify(state));
  }, [selectedWeek, selectedClient, selectedValet, selectedLocation]);

  const value = {
    selectedWeek,
    setSelectedWeek,
    selectedClient,
    setSelectedClient,
    selectedValet,
    setSelectedValet,
    selectedLocation,
    setSelectedLocation,
    // Convenience method to clear all selections
    clearSelections: () => {
      setSelectedClient(null);
      setSelectedValet(null);
      setSelectedLocation(null);
    }
  };

  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
};

// Custom hook to use the context
export const useSelection = () => {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
};

// Individual hooks for backward compatibility with UnifiedSelectionContext
export const useWeekSelection = () => {
  const context = useSelection();
  return {
    selectedWeek: context.selectedWeek,
    setSelectedWeek: context.setSelectedWeek,
    weekRange: context.selectedWeek ? {
      start: new Date(context.selectedWeek),
      end: new Date(new Date(context.selectedWeek).getTime() + 6 * 24 * 60 * 60 * 1000)
    } : null
  };
};

export const useClientSelection = () => {
  const context = useSelection();
  return {
    selectedClient: context.selectedClient,
    setSelectedClient: context.setSelectedClient,
    clearClient: () => context.setSelectedClient(null)
  };
};

export const useLocationSelection = () => {
  const context = useSelection();
  return {
    selectedLocation: context.selectedLocation,
    setSelectedLocation: context.setSelectedLocation,
    clearLocation: () => context.setSelectedLocation(null)
  };
};

export default SelectionContext;